/**
 * HiBFF V3 - Dashboard Data Hook
 * Fetches and caches dashboard data from the API
 */
import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// Fallback mock data for when API is unavailable
const fallbackData = {
  avas: [],
  conversations: [],
  moodData: [],
  badges: [],
  activityFeed: [],
  groups: [],
  notifications: [],
  socialScore: null,
};

export const useDashboardData = () => {
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState(fallbackData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all dashboard data in parallel
      const [
        avasResponse,
        conversationsResponse,
        scoreResponse,
      ] = await Promise.allSettled([
        apiClient.getAvas(),
        apiClient.getRecentConversations(5),
        apiClient.getCurrentScore(),
      ]);

      setData({
        avas: avasResponse.status === 'fulfilled' ? avasResponse.value : [],
        conversations: conversationsResponse.status === 'fulfilled' ? conversationsResponse.value : [],
        socialScore: scoreResponse.status === 'fulfilled' ? scoreResponse.value : null,
        // These will be populated when features are implemented
        moodData: [],
        badges: [],
        activityFeed: [],
        groups: [],
        notifications: [],
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.message);
      // Keep existing data on error
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const refresh = useCallback(() => {
    return fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    ...data,
    user,
    loading,
    error,
    refresh,
  };
};

// Hook for AVAs specifically
export const useAvas = () => {
  const [avas, setAvas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAvas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAvas();
      setAvas(response);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch AVAs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvas();
  }, [fetchAvas]);

  const createAva = async (avaData) => {
    const newAva = await apiClient.createAva(avaData);
    setAvas(prev => [...prev, newAva]);
    return newAva;
  };

  const updateAva = async (avaId, avaData) => {
    const updatedAva = await apiClient.updateAva(avaId, avaData);
    setAvas(prev => prev.map(a => a.ava_id === avaId ? updatedAva : a));
    return updatedAva;
  };

  const deleteAva = async (avaId) => {
    await apiClient.deleteAva(avaId);
    setAvas(prev => prev.filter(a => a.ava_id !== avaId));
  };

  return {
    avas,
    loading,
    error,
    refresh: fetchAvas,
    createAva,
    updateAva,
    deleteAva,
  };
};

// Hook for sessions/conversations
export const useSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getSessions(20);
      setSessions(response);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const createSession = async (sessionData) => {
    const newSession = await apiClient.createSession(sessionData);
    setSessions(prev => [newSession, ...prev]);
    return newSession;
  };

  const endSession = async (sessionId) => {
    await apiClient.endSession(sessionId);
    setSessions(prev => prev.map(s =>
      s.session_id === sessionId
        ? { ...s, status: 'completed' }
        : s
    ));
  };

  return {
    sessions,
    loading,
    error,
    refresh: fetchSessions,
    createSession,
    endSession,
  };
};

// Hook for social goals
export const useSocialGoals = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGoals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getGoals();
      setGoals(response);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch goals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const createGoal = async (goalData) => {
    const newGoal = await apiClient.createGoal(goalData);
    setGoals(prev => [...prev, newGoal]);
    return newGoal;
  };

  const updateGoal = async (goalId, goalData) => {
    const updatedGoal = await apiClient.updateGoal(goalId, goalData);
    setGoals(prev => prev.map(g => g.goal_id === goalId ? updatedGoal : g));
    return updatedGoal;
  };

  const deleteGoal = async (goalId) => {
    await apiClient.deleteGoal(goalId);
    setGoals(prev => prev.filter(g => g.goal_id !== goalId));
  };

  return {
    goals,
    loading,
    error,
    refresh: fetchGoals,
    createGoal,
    updateGoal,
    deleteGoal,
  };
};

export default useDashboardData;
